/**
 * Route: /wallets/:id
 *
 * Seit 09.2026 liegt diese Route bewusst AUSSERHALB von `_auth` — analog zu
 * /hotspots/:id (siehe dort). Die Wallet-Detailseite war in v1 öffentlich
 * (187.548 Aufrufe laut nginx-Log vor dem Relaunch), lag in v2 aber unter
 * `_auth` und lief für Besucher aus Suchmaschinen/alten Links gegen die
 * Login-Wand.
 *
 * GET /api/v2/wallets/:id liefert seitdem global (nicht mehr nur eigene
 * Wallets) und liefert Anonymen/fremden Betrachtern `is_owner: false` mit
 * `name`/`owned` auf null — WalletDetail blendet die besitzerexklusive
 * Aktionsreihe (Reimport/Tags/Rename/Remove/Claim) darüber bereits aus,
 * weil sie an `owned` hängt (siehe dort).
 *
 * Angemeldete Besucher bekommen weiterhin die volle AppShell mit
 * Seitenleiste, anonyme den schlanken PublicPageShell (Muster wie
 * hotspots.$id.tsx).
 */
import type { ReactNode } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useWallet } from '../features/wallets/useWallet'
import { WalletDetail } from '../features/wallets/WalletDetail'
import { PublicOrAppShell } from '../layouts/PublicOrAppShell'

export const Route = createFileRoute('/wallets/$id')({
  component: WalletDetailPage,
})

function WalletDetailPage() {
  const { t } = useTranslation('wallets')
  const { id } = Route.useParams()
  const { wallet, isLoading, isError } = useWallet(id)

  let content: ReactNode
  if (isLoading) {
    content = (
      <div aria-busy="true" aria-label={t('loading.wallet')} className="loading-placeholder">
        <div className="skeleton-line" style={{ width: 200, height: 24, margin: '24px auto' }} />
      </div>
    )
  } else if (isError || !wallet) {
    content = (
      <div className="error-state" role="alert">
        <p>{t('error.walletNotFound')}</p>
      </div>
    )
  } else {
    content = (
      <WalletDetail
        walletId={String(wallet.id)}
        // Defensiver Fallback: GET /api/v2/wallets/:id ist nicht im
        // OpenAPI-Kontrakt (siehe useWallet.ts), sol_addr ist dadurch nicht
        // typsicher garantiert non-null. Backend liefert es heute immer
        // (wallets_controller.rb#show), aber ohne Kontrakt waere eine
        // kuenftige Drift erst zur Laufzeit sichtbar.
        solAddr={wallet.sol_addr ?? ''}
        name={wallet.name ?? undefined}
        owned={wallet.owned ?? false}
        hotspotCount={wallet.hotspot_count}
      />
    )
  }

  return <PublicOrAppShell>{content}</PublicOrAppShell>
}
